import React, { useEffect, useState } from "react";
import Header from "components/v1/common/Header";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  addPaymentDetails,
  addPaymentStatus,
  getPaymentDetails,
} from "features/returnedProducts/returnedProductSlice";
import { getMerchantConfig } from "features/config/configSlice";
import { generatePayment, paymentStatus } from "utils/api";
import { getEncipherencode } from "utils/encipherencode";
import { versionedPath } from "utils/routes";
import { getApiErrorMessage } from "utils/apiError";
import { StyledGeneratePaymentWrapper, StyledPaymentBox } from "./styled";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function GeneratePayment() {
  const reduxPaymentDetails = useSelector(getPaymentDetails);
  const merchantConfig = useSelector(getMerchantConfig);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // const location = useLocation();
  const [params] = useSearchParams();
  const encipherencode = getEncipherencode(params);

  const transaction_id = params.get("txn_id");
  const order_id = params.get("order_id");
  const reference_id = params.get("reference_id");

  const [details, setDetails] = useState({});
  const [status, setStatus] = useState(
    order_id && reference_id ? "pending" : "generating"
  );
  const [autoRedirect, setAutoRedirect] = useState(true);
  const [paying, setPaying] = useState(true);

  // persist payment details so that they can be used after redirect
  useEffect(() => {
    if (reduxPaymentDetails && Object.keys(reduxPaymentDetails).length > 0) {
      setDetails(reduxPaymentDetails);
      sessionStorage.setItem(
        "payment_details",
        JSON.stringify(reduxPaymentDetails)
      );
    } else {
      const stored = sessionStorage.getItem("payment_details");
      if (stored) setDetails(JSON.parse(stored));
    }
  }, [reduxPaymentDetails]);

  // prevent back navigation
  // useEffect(() => {
  //   const url = encipherencode
  //     ? `${location.pathname}?encipherencode=${encipherencode}`
  //     : location.pathname;
  //   window.history.pushState(null, null, url);
  //   const onPopState = (e) => {
  //     e.preventDefault();
  //     window.history.pushState(null, null, url);
  //     toast.error("You cannot go back", {
  //       position: toast.POSITION.TOP_RIGHT,
  //       autoClose: 2000,
  //     });
  //   };
  //   window.addEventListener("popstate", onPopState);

  //   return () => window.removeEventListener("popstate", onPopState);
  // }, []);

  useEffect(() => {
    setTimeout(() => {
      setAutoRedirect(false);
    }, 3000);
  }, []);

  // redirect to payment gateway with slight delay so that page is visible
  useEffect(() => {
    if (!reference_id && details && details.payment_url) {
      const timer = setTimeout(() => {
        if (details.payu) {
          const formElement = document.createElement("form");
          formElement.setAttribute("method", "post");
          formElement.setAttribute("action", details.redirect_url);
          for (const [key, value] of Object.entries(details.payu_data_dict)) {
            const hiddenField = document.createElement("input");
            hiddenField.setAttribute("name", key);
            hiddenField.setAttribute("value", value);
            formElement.appendChild(hiddenField);
          }
          document.body.appendChild(formElement);
          formElement.submit();
        } else {
          window.location.href = details.payment_url;
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [details, reference_id]);

  // polling payment status after returning from gateway
  useEffect(() => {
    if (order_id && reference_id && merchantConfig.merchant_id) {
      let intervalId;
      const checkStatus = () => {
        paymentStatus(
          merchantConfig.merchant_id,
          transaction_id,
          reference_id,
          order_id,
          false,
          true
        )
          .then((resp) => {
            sessionStorage.setItem("access_token", resp.data.access_token);
            sessionStorage.setItem("payment_status", JSON.stringify(resp.data));
            dispatch(addPaymentStatus(resp.data));
            const paymentStatusValue = String(
              resp.data.payment_status || ""
            ).toLowerCase();
            const paymentSucceeded =
              resp.data.success === true ||
              ["completed", "success", "paid"].includes(paymentStatusValue);

            if (paymentStatusValue === "failed" && !resp.data.success) {
              setStatus("failed");
              clearInterval(intervalId);
            } else if (paymentSucceeded) {
              setStatus("success");
              clearInterval(intervalId);
              setTimeout(() => {
                let url = versionedPath("/success");
                const queryParams = new URLSearchParams();
                if (encipherencode) {
                  queryParams.set("encipherencode", encipherencode);
                }
                const requestId =
                  resp.data.return_request_id ||
                  resp.data.request_id ||
                  details.return_request_id;
                if (requestId) {
                  queryParams.set("request_id", requestId);
                }
                if (resp.data.reference_id || reference_id) {
                  queryParams.set(
                    "reference_id",
                    resp.data.reference_id || reference_id
                  );
                }
                const queryString = queryParams.toString();
                if (queryString) url += `?${queryString}`;
                navigate(url);
              }, 1000);
            } else {
              setStatus("pending");
            }
            setDetails((prev) => ({ ...prev, ...resp.data }));
          })
          .catch(() => {
            setStatus("failed");
            clearInterval(intervalId);
          });
      };
      checkStatus();
      intervalId = setInterval(checkStatus, 5000);
      return () => clearInterval(intervalId);
    }
  }, [order_id, reference_id, merchantConfig.merchant_id]);

  const payAgain = () => {
    setPaying(true);
    generatePayment(
      details.return_request_id,
      details.transaction_id,
      details.reference_id
    )
      .then((resp) => {
        dispatch(addPaymentDetails(resp.data));
        if (resp.data.payu) {
          const formElement = document.createElement("form");
          formElement.setAttribute("method", "post");
          formElement.setAttribute("action", resp.data.redirect_url);
          for (const [key, value] of Object.entries(resp.data.payu_data_dict)) {
            const hiddenField = document.createElement("input");
            hiddenField.setAttribute("name", key);
            hiddenField.setAttribute("value", value);
            formElement.appendChild(hiddenField);
          }
          document.body.appendChild(formElement);
          formElement.submit();
        } else if (resp.data.payment_url) {
          window.location.href = resp.data.payment_url;
        }
        setTimeout(() => {
          setPaying(false);
        }, 1000);
      })
      .catch((err) => {
        toast.error(
          getApiErrorMessage(
            err,
            "Payment cannot be generated. Please try again"
          ),
          {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 6000,
            hideProgressBar: false,
            closeOnClick: true,
          }
        );
      });
  };

  const statusText = {
    success: {
      text: "Successful payment",
      color: "#16AC37",
      background: "#16AC3733",
    },
    pending: { text: "Pending...", color: "#F6C343", background: "#F6C34333" },
    generating: {
      text: "Generating Payment Link...",
      color: "#F6C343",
      background: "#F6C34333",
    },
    failed: { text: "Failed", color: "#F1416C", background: "#F1416C33" },
  }[status];

  return (
    <div>
      <Header />
      <StyledGeneratePaymentWrapper>
        <StyledPaymentBox
          color={statusText.color}
          background={statusText.background}
          primary_color={merchantConfig.primary_theme_color}
        >
          <div className="status">
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="status-text">
                {statusText.text}
                {(status === "pending" || status === "generating") && (
                  <ClipLoader
                    size={16}
                    color={statusText.color}
                    cssOverride={{ marginLeft: "8px" }}
                  />
                )}
              </span>
              {status === "failed" && (
                <button
                  className="pay-again"
                  onClick={payAgain}
                  disabled={paying}
                >
                  {paying ? "Generating..." : "Pay again"}
                </button>
              )}
            </div>
            <span
              style={{
                display: autoRedirect ? "none" : "block",
                margin: "0px 1rem 10px",
                fontSize: "12px",
              }}
            >
              If you are not redirected automatically then please click{" "}
              <a href={details?.payment_url}>here</a>
            </span>
          </div>
          <div className="details">
            {details?.amount !== undefined && (
              <div>
                Amount:{" "}
                <span style={{ fontWeight: "600" }}>₹ {details.amount}</span>
              </div>
            )}
            <div>Payment Type: Prepaid</div>
            {details?.id && <div>Transaction ID: {details.id}</div>}
            {details?.payment_id && <div>Payment ID: {details.payment_id}</div>}
            {details?.timestamp && (
              <div>
                Timestamp: {new Date(details.timestamp).toLocaleString()}
              </div>
            )}
          </div>
        </StyledPaymentBox>
        {status === "success" && (
          <p style={{ marginTop: "1.5rem" }}>
            Redirecting you to success page...{" "}
            <ClipLoader size={14} cssOverride={{ marginLeft: "4px" }} />
          </p>
        )}
      </StyledGeneratePaymentWrapper>
    </div>
  );
}

export default GeneratePayment;
